import { roles } from 'feature/feature-roles';
import { useFirebaseUser } from 'internal/firebase';

import { useIsLive, useSessionData } from './datastore';
import { getDeploymentConfig } from './util';

let global_capability_map: Record<string, any> = {};
let global_roles: Record<string, any> = roles;

const deploymentConfig = getDeploymentConfig();

export function useIsLocalhostAdmin() {
    const fbUser = useFirebaseUser();
    const isLive = useIsLive();
    if (!fbUser || !isLive) {
        return false;
    }
    const emailDomain = fbUser.email?.split('@')?.[1];
    return deploymentConfig.adminEmailDomain && emailDomain === deploymentConfig.adminEmailDomain;
}

export function useMyRoles(): string[] | null {
    const isLocalhostAdmin = useIsLocalhostAdmin();
    const roles = useSessionData('roles');
    if (isLocalhostAdmin) {
        return ['Owner', 'Developer'];
    } else {
        return roles;
    }
}

export function useIsAdmin() {
    const localHostAdmin = useIsLocalhostAdmin();
    const myRoles = useMyRoles();
    return localHostAdmin || (myRoles && myRoles.length > 0);
}

export function useHasCapability(capability?: string) {
    const myRoles = useMyRoles();
    const localHostAdmin = useIsLocalhostAdmin();

    if (localHostAdmin) {
        return true;
    }
    if (!myRoles || !capability) {
        return null;
    }

    for (const role of myRoles) {
        const roleInfo = global_roles?.[role];
        if (roleInfo?.allCapabilities || role === 'Owner') {
            return true;
        }
        if (global_capability_map?.[role]?.[capability]) {
            return true;
        }
    }
    return false;
}

export function makeCapabilityMap(roles: Record<string, any>) {
    const capability_map: Record<string, Record<string, boolean>> = {};
    Object.keys(roles).forEach((roleKey) => {
        const role = roles[roleKey];
        if (!capability_map[roleKey]) {
            capability_map[roleKey] = {};
        }
        role?.can?.forEach((capability) => {
            capability_map[roleKey][capability] = true;
        });
        if (role.inherits) {
            role.inherits.forEach((inheritedRoleKey) => {
                const inheritedRole = roles[inheritedRoleKey];
                inheritedRole?.can?.forEach((capability) => {
                    if (!capability_map[roleKey]) {
                        capability_map[roleKey] = {};
                    }
                    capability_map[roleKey][capability] = true;
                });
            });
        }
    });
    return capability_map;
}

export function getRoles() {
    return roles;
}

export function mergeRoles(oldRoles: Record<string, any>, newRoles: Record<string, any>) {
    const mergedRoles = { ...oldRoles, ...newRoles };
    Object.keys(mergedRoles).forEach((roleKey) => {
        if (oldRoles[roleKey] && newRoles[roleKey]) {
            const oldRole = oldRoles[roleKey];
            const newRole = newRoles[roleKey];
            const mergedRole = { ...oldRole, ...newRole };
            mergedRole.can = [...(oldRole.can ?? []), ...(newRole.can ?? [])];
            mergedRole.inherits = [...(oldRole.inherits ?? []), ...(newRole.inherits ?? [])];
            mergedRoles[roleKey] = mergedRole;
        }
    });
    return mergedRoles;
}

export function extendRoles(newRoles: Record<string, any>) {
    const mergedRoles = mergeRoles(roles, newRoles);
    global_roles = mergedRoles;
    global_capability_map = makeCapabilityMap(mergedRoles);
}
